import { Box, Button, Paper, TextField, Tooltip, Typography, FormControlLabel, MenuItem, CircularProgress } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../utils/methods';
import AnimationPage from '../../pages/AnimationPage';
import { useForm } from 'react-hook-form';
import { getAllChatMembers, getChatHistory, updateChatRoomClose } from '../../api/chat';
import { Client } from '@stomp/stompjs';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment';
import { getChatUrl } from '../../api/mainCaller';
import useUserAuth from '../../utils/useUserAuth';

const ChatBranch = () => {
  const { t } = useTranslation();
  const canChat = useUserAuth('COMMUNITY_CHAT');
  const token = useSelector(function (state) {
    return state.user.user.token;
  });
  const toast = useToast();
  // const { handleSubmit, control, reset, watch, getValues, setValue } = useForm({ defaultValues: initialQuery });
  const navigate = useNavigate();
  const [toggleDotMenu, setToggleDotMenu] = useState(null);
  const [chatMembers, setChatMembers] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [content, setContent] = useState('');
  const [client, setClient] = useState(null);
  const [connected, setConnected] = useState(false);
  const [senderId, setSenderId] = useState(null);
  const [recipientId, setRecipientId] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [scroolToBottom, setScroolToBottom] = useState(true);
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    sortOrder: 'ASCENDING',
    sortType: 'LATEST',
    recipientId: null,
  });
  const bottomRef = useRef(null);
  const topRef = useRef(null);
  const recipientIdRef = useRef(null);

  const decoded = jwtDecode(token);
  const { userId, sub } = decoded;

  const getChatHistories = async (id, scrollCall) => {
    try {
      setIsLoading(true);

      // Fetch chat history data
      const data = await getChatHistory(token, {
        ...query,
        recipientId: id,
        requestType: 'admin',
        page: scrollCall ? query.page + 1 : 1,
      });

      // If there are no more messages to fetch, prevent further pagination
      if (data.content.length === 0) {
        console.log('No more messages to load');
        setChatHistory([]);
        return;
      }

      // Update chat history
      setChatHistory(scrollCall ? [...data.content.reverse(), ...chatHistory] : data.content.reverse());
    } catch (error) {
      console.error('API call failed:', error);
      toast.error(errorMessages(error));
    } finally {
      setIsLoading(false);
    }
  };

  const getMembers = async (type, id) => {
    try {
      const datas = await getAllChatMembers(token);
      type
        ? setChatMembers(
            datas
              .map(data => {
                if (data.memberUserId === id) {
                  return { ...data, read: false };
                } else {
                  return data;
                }
              })
              ?.sort((a, b) => Number(a.read) - Number(b.read)),
          )
        : setChatMembers(datas?.sort((a, b) => Number(a.read) - Number(b.read)));
    } catch (error) {
      console.error('API call failed:', error);
      if (error?.response?.status === 406) {
        return null;
      }
      toast.error(errorMessages(error));
    }
  };

  const connect = id => {
    if (!client || !connected || !id) return;
    setRecipientId(id);
    recipientIdRef.current = id;
    setChatMembers(
      chatMembers.map(chat => {
        if (chat.memberUserId === id) {
          return { ...chat, read: true };
        } else {
          return chat;
        }
      }),
    );

    // const senderId = id;

    // client.subscribe(`/user/${userId}/queue/messages`, message => {
    //   Number(JSON.parse(message.body).senderId) === Number(senderId) &&
    //     setChatHistory(prevHistory => [...prevHistory, JSON.parse(message.body)]);
    // });
    // setSenderId(userId);
    // setRecipientId(senderId);
  };

  const messageSend = () => {
    if (!client || !content.trim() || !connected) return;

    const chatMessage = {
      senderId: senderId,
      recipientId: recipientId,
      content: content,
    };

    client.publish({
      destination: '/app/chat',
      body: JSON.stringify(chatMessage),
    });
    setChatHistory(prevHistory => [
      ...prevHistory,
      { ...chatMessage, sentDateTime: moment().format('YYYY-MM-DD HH:mm:ss') },
    ]);
    setContent('');
    setScroolToBottom(true);
  };

  useEffect(() => {
    const currentRef = topRef.current;

    const handleScroll = async () => {
      if (currentRef.scrollTop === 0 && recipientId) {
        const previousScrollHeight = currentRef.scrollHeight;

        // Fetch the next page of chat history
        const data = await getChatHistory(token, {
          ...query,
          recipientId,
          requestType: 'admin',
          page: query.page + 1,
        });

        if (data.content.length > 0) {
          // Increment page and update chat history only if there are messages
          setQuery(prevQuery => ({
            ...prevQuery,
            page: prevQuery.page + 1,
          }));
          setChatHistory([...data.content.reverse(), ...chatHistory]);
        } else {
          console.log('No more messages to fetch');
        }

        // Adjust scroll position after fetching
        setTimeout(() => {
          if (currentRef) {
            currentRef.scrollTop = currentRef.scrollHeight - previousScrollHeight;
          }
        }, 100);
      }
    };

    // Add the scroll event listener
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
    }

    // Cleanup the event listener when component unmounts
    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [recipientId, query, chatHistory]);

  useEffect(() => {
    const stompClient = new Client({
      brokerURL:
        getChatUrl()
          .replace(/^https/, 'wss')
          .replace(/^http/, 'ws') + '/ws',
      connectHeaders: {
        Authorization: `${token}`,
      },
      onConnect: () => {
        console.log('Connected to broker');
        setConnected(true);
        stompClient.subscribe(`/user/${userId}/queue/messages`, message => {
          const parsed = JSON.parse(message.body);
          if (Number(parsed.senderId) === Number(recipientIdRef.current)) {
            setChatHistory(prevHistory => [...prevHistory, parsed]);
          } else {
            getMembers(true, parsed.senderId);
          }
        });
      },
      onStompError: frame => {
        console.error('STOMP Error:', frame);
      },
    });

    stompClient.activate();
    setClient(stompClient);
    setSenderId(userId);

    return () => {
      if (stompClient) stompClient.deactivate();
    };
  }, []);

  useEffect(() => {
    getMembers(false, null);
  }, []);

  useEffect(() => {
    if (scroolToBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  const handleChatRoomClose = async roomId => {
    try {
      console.log('roomId', roomId);
      console.log('senderId', senderId);
      console.log('recipientId', recipientId);
      const data = {
        senderId: senderId,
        recipientId: recipientId,
        chatRoomId: roomId, // 이 부분은 chat 객체에서 chatRoomId를 가져와야 합니다
      };

      await updateChatRoomClose(data, token);

      // 채팅 나가기 후 상태 초기화
      setRecipientId(null);
      setScroolToBottom(true);
      setChatHistory([]);
      setContent('');
      setQuery({
        page: 1,
        limit: 10,
        sortOrder: 'ASCENDING',
        sortType: 'LATEST',
        recipientId: null,
      });

      // 채팅 멤버 목록 새로고침
      getMembers(false, null);

      toast.success('채팅에서 나갔습니다');
    } catch (error) {
      console.error('채팅 나가기 실패:', error);
      toast.error(errorMessages(error));
    }
  };

  return (
    <AnimationPage>
      <Box
        component={Paper}
        elavation={2}
        sx={{
          background: '#fff',
          borderRadius: '10px',
          padding: '30px 40px',
          width: 'auto',
          height: '100%',
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
            gap: '50px',
          }}>
          {/* <ChatTest /> */}
          <Box sx={{ display: 'flex', gap: '28px', height: '738px' }}>
            <Box
              sx={{
                minWidth: '400px',
                height: '100%',
                border: '1px solid #ececec',
                borderRadius: '8px',
                overflow: 'auto',
                position: 'relative',
              }}>
              <Box
                sx={{
                  height: '66px',
                  borderBottom: '1px solid #ececec',
                  padding: '24px 28px',
                  position: 'sticky',
                  top: '0px',
                  background: '#fff',
                }}>
                <Typography sx={{ fontWeight: '600', fontSize: '15px' }}>
                  참여 인원 ({chatMembers?.length || 0} 명)
                </Typography>
              </Box>
              <Box sx={{ padding: '24px 28px' }}>
                {chatMembers.map((chat, index) => {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: '20px',
                        cursor: 'pointer',
                        justifyContent: 'space-between',
                        backgroundColor: '#fff',
                        position: 'relative',
                      }}>
                      {!chat?.read && (
                        <Box
                          sx={{
                            height: '10px',
                            width: '10px',
                            background: 'red',
                            borderRadius: '50%',
                            position: 'absolute',
                            left: '0px',
                            top: '0px',
                          }}></Box>
                      )}
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                        onClick={() => {
                          if (recipientId !== chat.memberUserId) {
                            setQuery({
                              page: 1,
                              limit: 10,
                              sortOrder: 'ASCENDING',
                              sortType: 'LATEST',
                              recipientId: null,
                            });
                            setGuestName(chat.memberName);
                            connect(chat.memberUserId);
                            setScroolToBottom(true);
                            getChatHistories(chat.memberUserId, false);
                          }
                        }}>
                        <img
                          src={chat.profileImage ? chat.profileImage : '/images/chat-guest.svg'}
                          width={40}
                          height={40}
                          style={{ borderRadius: '50%' }}
                          alt="checked"
                        />
                        <Typography sx={{ fontWeight: '500', fontSize: '14px' }}>{chat.memberName}</Typography>
                      </Box>
                      <Box
                        sx={{ cursor: 'pointer' }}
                        // onClick={() => {
                        //   setToggleDotMenu(!toggleDotMenu);
                        // }}
                        onMouseEnter={() =>
                          setToggleDotMenu(chat.memberUserId === recipientId ? chat.memberUserId : null)
                        } // Show the menu when hovering
                        onMouseLeave={() => setToggleDotMenu(null)} // Hide the menu when mouse leaves
                      >
                        {toggleDotMenu !== chat.memberUserId ? (
                          <img src="/images/dots.svg" alt="3 dots menu" />
                        ) : (
                          <Box
                            sx={{
                              width: '112px',
                              height: '32px',
                              borderRadius: '8px',
                              border: '1px solid #ececec',
                              background: '#fff',
                              display: 'flex',
                              padding: '5px',
                              gap: '5px',
                            }}>
                            <img
                              src="/images/close.svg"
                              alt="X icon"
                              onClick={() => handleChatRoomClose(chat.chatRoomId)}
                            />
                            <Typography>채팅 나가기</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Box
              sx={{
                width: '800px',
                height: '100%',
                border: '1px solid #ececec',
                borderRadius: '8px',
                background: '#fff',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
              }}>
              {isLoading && (
                <Box
                  sx={{
                    background: '#00000010',
                    height: '100%',
                    width: '100%',
                    top: '0',
                    left: '0',
                    position: 'absolute',
                  }}>
                  <CircularProgress
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </Box>
              )}
              <Box
                sx={{
                  width: '800px',
                  height: '646px',
                  padding: '35px 28px',
                  wordBreak: 'break-all',
                  overflow: 'auto',
                  position: 'relative',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#0f7f85',
                    borderRadius: '0px',
                    '&:hover': {
                      backgroundColor: '#0A585D',
                    },
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                  },
                }}
                ref={topRef}>
                {recipientId &&
                  chatHistory?.map((chat, index) => (
                    <Box key={index}>
                      {chat?.sentDateTime?.split(' ')[0] !== chatHistory?.[index - 1]?.sentDateTime?.split(' ')[0] ||
                      chatHistory?.[index - 1]?.sentDateTime?.split(' ')[0] === undefined ? (
                        <Box sx={{ width: '100%' }}>
                          <Typography
                            sx={{
                              textAlign: 'center',
                              color: '#999999',
                              fontSize: '15px',
                              padding: '6px 12px',
                              background: '#EBEBEF',
                              borderRadius: '25px',
                              maxWidth: '150px',
                              margin: 'auto',
                            }}>
                            {moment(chat.sentDateTime).format('YYYY.MM.DD ddd')}
                          </Typography>
                        </Box>
                      ) : null}
                      {chat.senderId === userId ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mb: '14px',
                            width: '100%',
                            alignItems: 'flex-start',
                          }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', padding: '8px', gap: '8px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                              <Typography sx={{ color: '#787878' }}>
                                {moment(chat.sentDateTime).subtract(1, 'years').format('A hh:mm')}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                padding: '10px 12px',
                                background: '#0f7f85',
                                borderRadius: '12px 0px 12px 12px',
                                color: '#fff',
                                whiteSpace: 'pre-wrap',
                              }}>
                              {chat.content}
                            </Typography>
                          </Box>
                          <img src="/images/chat-owner.svg" alt="chat owner" style={{ marginLeft: '12px' }} />
                        </Box>
                      ) : (
                        <Box
                          sx={{ display: 'flex', justifyContent: 'flex-start', mb: '28px', alignItems: 'flex-start' }}>
                          <img
                            src={chat.senderProfileImage ? chat.senderProfileImage : '/images/chat-guest.svg'}
                            width={40}
                            height={40}
                            style={{ borderRadius: '50%', marginRight: '12px' }}
                            alt="checked"
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column', padding: '8px', gap: '8px' }}>
                            <Box sx={{ display: 'flex', gap: '8px' }}>
                              <Typography>{!isLoading && guestName}</Typography>
                              <Typography sx={{ color: '#787878' }}>
                                {moment(chat.sentDateTime).subtract(1, 'years').format('A hh:mm')}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                padding: '10px 12px',
                                background: '#ececec',
                                borderRadius: '0px 12px 12px 12px',
                                whiteSpace: 'pre-wrap',
                              }}>
                              {chat.content}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                <div ref={bottomRef} />
              </Box>
              {recipientId && (
                <Box
                  sx={{
                    // height: '92px',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    borderTop: '1px solid #ccc',
                    padding: '20px 25px',
                  }}>
                  <TextField
                    disabled={!canChat}
                    placeholder={canChat ? '내용을 입력하여 주세요' : '채팅 권한이 없습니다'}
                    sx={{
                      minWidth: '744px',
                      border: 'none',
                      '& .MuiInputBase-input': {
                        padding: '0px 42px 0px 15px !important',
                      },
                    }}
                    multiline
                    InputProps={{
                      style: {
                        borderRadius: '25px',
                        padding: '0px !important',
                      },
                    }}
                    value={content}
                    onKeyDown={e => {
                      if (e.nativeEvent.isComposing) return;
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        messageSend();
                      } else if (e.key === 'Enter' && e.shiftKey) {
                        e.preventDefault();
                        const target = e.target;
                        const { selectionStart, selectionEnd } = target;
                        setContent(
                          prevContent => `${prevContent.slice(0, selectionStart)}\n${prevContent.slice(selectionEnd)}`,
                        );
                        setTimeout(() => {
                          target.selectionStart = target.selectionEnd = selectionStart + 1;
                        }, 0);
                      }
                    }}
                    onChange={e => setContent(e.target.value)}
                  />

                  <Tooltip title={!canChat ? '권한이 없습니다' : ''} arrow>
                  <Button
                    disabled={!(content.trim() !== '')}
                    sx={{
                      position: 'absolute', right: '35px', cursor: 'pointer', padding: 0, height: '25px',
                      ...(!canChat ? { opacity: 0.5, cursor: 'default' } : {}),
                    }}
                    onClick={canChat ? messageSend : undefined}>
                    {content !== '' ? (
                      <img src="/images/icons/chat-send-button.svg" alt="send icon" style={{ height: '100%' }} />
                    ) : (
                      <img
                        src="/images/icons/chat-send-button-inactive.svg"
                        alt="send icon"
                        style={{ height: '100%' }}
                      />
                    )}
                  </Button>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </AnimationPage>
  );
};

export default ChatBranch;
